import { useMemo, useState } from 'react';
import { useAdminI18n } from '../../../lib/i18n/use-admin-i18n';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { PaginationState, SortingState } from '@tanstack/react-table';
import { DynamicTable } from '../../../components/custom/table/DynamicTable';
import type {
  DynamicTableColumnConfig,
  DynamicTableControllers,
} from '../../../components/custom/table/dynamic-table-types';
import type { TenantDto } from '@cms/models';
import { SearchFilter, StatusFilter } from '../../../components/custom/filters';
import { saasApi } from '../../../lib/api/saas.api';
import {
  AdminTableRowActions,
  PagePrimaryActionButton,
} from '../../../components/common/AdminTableRowActions';
import { TenantFormModal, type TenantFormModalMode } from './TenantFormModal';

const TENANT_STATUS_VALUES = ['active', 'suspended'] as const;

interface TenantsTableProps {
  rows: TenantDto[];
  controllers?: DynamicTableControllers;
  rowOffset: number;
  onView: (tenant: TenantDto) => void;
  onEdit: (tenant: TenantDto) => void;
  onDelete: (tenant: TenantDto) => void;
  isDeleting: boolean;
  isLoading: boolean;
}

function TenantsTable(props: Readonly<TenantsTableProps>) {
  const { rows, controllers, rowOffset, onView, onEdit, onDelete, isDeleting, isLoading } = props;
  const { col, act, page, tableLoading, t, language } = useAdminI18n();
  const tenantStatusOptions = useMemo(
    () =>
      TENANT_STATUS_VALUES.map(value => ({
        value,
        label: t(`shared.status.${value}`),
      })),
    [t, language],
  );

  const columns: DynamicTableColumnConfig<TenantDto>[] = useMemo(
    () => [
    {
      id: 'no',
      header: col('no'),
      columnType: 'number',
      cell: ({ index }) => rowOffset + index + 1,
      widthMode: 'fit',
      size: 64,
      meta: { className: 'w-16 text-muted-foreground' },
    },
    {
      id: 'name',
      accessorKey: 'name',
      header: col('name'),
      sortable: true,
      columnType: 'text',
    },
    {
      id: 'code',
      accessorKey: 'code',
      header: col('code'),
      sortable: true,
      columnType: 'text',
    },
    {
      id: 'url',
      accessorKey: 'url',
      header: col('url'),
      columnType: 'text',
      cell: ({ row }) => {
        const url = (row as { url?: string | null }).url;
        if (!url?.trim()) return '—';
        return (
          <a
            href={url.startsWith('http') ? url : `https://${url}`}
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary underline truncate block max-w-[180px]"
          >
            {url}
          </a>
        );
      },
    },
    {
      id: 'notes',
      accessorKey: 'notes',
      header: col('notes'),
      columnType: 'text',
      meta: { className: 'max-w-[200px] truncate' },
    },
    {
      id: 'contact',
      header: col('contact'),
      columnType: 'text',
      cell: ({ row }) => {
        const contact = (row as { contact?: Record<string, unknown> | null }).contact;
        if (!contact || typeof contact !== 'object') return '—';
        const asString = (v: unknown): string => (typeof v === 'string' ? v : '');
        const parts: string[] = [];
        const rep = asString(contact.representative).trim();
        if (rep) parts.push(rep);
        const phone = asString(contact.phone).trim();
        if (phone) parts.push(phone);
        if (parts.length === 0) return '—';
        return parts.join(' · ');
      },
    },
    {
      id: 'status',
      accessorKey: 'status',
      header: col('status'),
      sortable: true,
      columnType: 'badge',
      displayLabelOptions: tenantStatusOptions,
      widthMode: 'fit',
      size: 120,
      meta: { className: 'min-w-[120px]' },
    },
    {
      id: 'actions',
      header: col('actions'),
      columnType: 'actions',
      cell: ({ row }) => (
        <AdminTableRowActions
          act={act}
          onView={() => onView(row)}
          onEdit={() => onEdit(row)}
          onDelete={() => onDelete(row)}
          isDeleting={isDeleting}
        />
      ),
      widthMode: 'fit',
      size: 220,
      minSize: 180,
      meta: { className: 'min-w-[180px] text-right' },
    },
    ],
    [act, col, isDeleting, onDelete, onEdit, onView, rowOffset, tenantStatusOptions],
  );

  return (
    <DynamicTable<TenantDto>
      features={{
        columns: true,
        rows: true,
        sorting: true,
        pagination: true,
        globalFilter: false,
        stripedRows: true,
        fontSize: 'xs',
        compactPadding: true,
      }}
      columns={columns}
      rows={{
        data: rows,
        getRowId: row => row.id as string,
      }}
      controllers={controllers}
      isLoading={isLoading}
      loadingMessage={tableLoading}
      emptyMessage={page('tenants', 'empty')}
    />
  );
}

type TenantStatusFilter = (typeof TENANT_STATUS_VALUES)[number] | '';

export function TenantsPage() {
  const { col, page, allStatusesOption, t, language } = useAdminI18n();
  const tenantStatusOptions = useMemo(
    () =>
      TENANT_STATUS_VALUES.map(value => ({
        value,
        label: t(`shared.status.${value}`),
      })),
    [t, language],
  );
  const queryClient = useQueryClient();
  const [sorting, setSorting] = useState<SortingState>([{ id: 'code', desc: false }]);
  const [pagination, setPagination] = useState<PaginationState>({ pageIndex: 0, pageSize: 25 });
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<TenantStatusFilter>('');
  const [formModal, setFormModal] = useState<{
    open: boolean;
    mode: TenantFormModalMode;
    tenant: TenantDto | null;
  }>({ open: false, mode: 'create', tenant: null });

  const { data: tenants = [], isLoading } = useQuery({
    queryKey: ['saas-tenants'],
    queryFn: () => saasApi.getTenants(),
  });

  const filteredTenants = useMemo(() => {
    let list = tenants;
    const term = search.trim().toLowerCase();
    if (term) {
      list = list.filter(t => {
        const code = t.code?.toLowerCase() ?? '';
        const name = t.name?.toLowerCase() ?? '';
        return code.includes(term) || name.includes(term);
      });
    }
    if (statusFilter) {
      list = list.filter(t => (t.status ?? '') === statusFilter);
    }
    return list;
  }, [tenants, search, statusFilter]);

  const sortedTenants = useMemo(() => {
    if (!sorting.length) return filteredTenants;
    const { id, desc } = sorting[0]!;
    const field = id === 'name' || id === 'code' || id === 'status' ? id : 'code';
    return [...filteredTenants].sort((a, b) => {
      const av = (a as Record<string, unknown>)[field];
      const bv = (b as Record<string, unknown>)[field];
      const as = (av ?? '') as string;
      const bs = (bv ?? '') as string;
      const cmp = as.localeCompare(bs);
      return desc ? -cmp : cmp;
    });
  }, [filteredTenants, sorting]);

  const totalRecords = sortedTenants.length;
  const pageSize = pagination.pageSize;
  const pageCount = Math.max(1, Math.ceil(Math.max(1, totalRecords) / pageSize));
  const safePageIndex = Math.min(pagination.pageIndex, pageCount - 1);

  const pageState: PaginationState = {
    pageIndex: safePageIndex,
    pageSize,
  };

  const paginatedTenants = useMemo(() => {
    const start = safePageIndex * pageSize;
    return sortedTenants.slice(start, start + pageSize);
  }, [sortedTenants, safePageIndex, pageSize]);

  const deleteMutation = useMutation({
    mutationFn: (id: string) => saasApi.deleteTenant(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['saas-tenants'] });
    },
  });

  const handleDelete = async (tenant: TenantDto) => {
    if (!globalThis.confirm(`Are you sure you want to delete tenant "${tenant.name}"?`)) {
      return;
    }
    try {
      await deleteMutation.mutateAsync(tenant.id as string);
    } catch (error: unknown) {
      const message =
        error instanceof Error ? error.message : 'Failed to delete tenant';
      alert(message);
    }
  };

  const controllers: DynamicTableControllers = {
    sort: {
      state: sorting,
      onSortingChange: updater => {
        setSorting(prev => (typeof updater === 'function' ? updater(prev) : updater));
        setPagination(prev => ({ ...prev, pageIndex: 0 }));
      },
    },
    pagination: {
      state: pageState,
      pageCount,
      rowCount: totalRecords,
      onPaginationChange: updater => {
        setPagination(prev => (typeof updater === 'function' ? updater(prev) : updater));
      },
    },
  };

  return (
    <div className="space-y-2 p-2">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-3xl font-bold">{page('tenants', 'title')}</h1>
          <p className="mt-1 text-sm text-muted-foreground">{page('tenants', 'subtitle')}</p>
        </div>
        <PagePrimaryActionButton
          onClick={() => setFormModal({ open: true, mode: 'create', tenant: null })}
        >
          {page('tenants', 'new')}
        </PagePrimaryActionButton>
      </div>

      <div className="glass-card overflow-hidden">
        <div className="flex flex-wrap items-end gap-2 border-b border-border p-3">
          <SearchFilter
            id="tenants-name"
            label={col('name')}
            value={search}
            onChange={v => {
              setSearch(v);
              setPagination(prev => ({ ...prev, pageIndex: 0 }));
            }}
            placeholder={page('tenants', 'search_placeholder')}
            className="[&_input]:h-9 [&_input]:py-1.5 [&_input]:text-sm"
          />
          <StatusFilter
            id="tenants-status"
            value={statusFilter}
            onChange={v => {
              setStatusFilter(v as TenantStatusFilter);
              setPagination(prev => ({ ...prev, pageIndex: 0 }));
            }}
            options={tenantStatusOptions}
            emptyOption={allStatusesOption}
            className="[&_button]:h-9 [&_input]:h-9 [&_input]:py-1.5 [&_input]:text-sm"
          />
        </div>
        <TenantsTable
          rows={paginatedTenants}
          controllers={controllers}
          rowOffset={pageState.pageIndex * pageState.pageSize}
          onView={t =>
            setFormModal({ open: true, mode: 'view', tenant: t })
          }
          onEdit={t =>
            setFormModal({ open: true, mode: 'edit', tenant: t })
          }
          onDelete={handleDelete}
          isDeleting={deleteMutation.isPending}
          isLoading={isLoading}
        />
      </div>

      <TenantFormModal
        open={formModal.open}
        onClose={() =>
          setFormModal(prev => ({ ...prev, open: false, tenant: null }))
        }
        mode={formModal.mode}
        tenant={formModal.tenant}
        onSuccess={() =>
          setFormModal(prev => ({ ...prev, open: false, tenant: null }))
        }
      />
    </div>
  );
}
